import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

interface User {
  id: string;
  email: string;
  displayName: string;
  tenantId: string;
  role: string;
  mfaEnabled: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  requiresMfa: boolean;
  mfaToken: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => void;
  ensureValidToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

function parseJwt(token: string): Record<string, any> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000 - 60000; // 1 min buffer
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = sessionStorage.getItem('auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.accessToken && !isTokenExpired(parsed.accessToken)) {
          return { ...parsed, requiresMfa: false, mfaToken: null };
        }
      } catch { /* ignore */ }
    }
    return {
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      requiresMfa: false,
      mfaToken: null,
    };
  });

  useEffect(() => {
    if (state.isAuthenticated) {
      sessionStorage.setItem('auth', JSON.stringify({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: true,
      }));
    } else {
      sessionStorage.removeItem('auth');
    }
  }, [state.isAuthenticated, state.accessToken, state.refreshToken, state.user]);

  const applyTokenResponse = useCallback((data: any) => {
    setState({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: {
        id: '',
        email: '',
        displayName: data.displayName || '',
        tenantId: data.tenantId || '',
        role: data.role || '',
        mfaEnabled: false,
      },
      isAuthenticated: true,
      requiresMfa: false,
      mfaToken: null,
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await axios.post(`${API_URL}/auth/login`, { email, password });

    if (data.requiresMfa) {
      setState(prev => ({
        ...prev,
        requiresMfa: true,
        mfaToken: data.mfaToken,
      }));
      return;
    }

    // Admin must set up MFA before accessing the app
    if (data.mfaRequired) {
      throw new Error(data.message || 'MFA setup is required for administrator accounts.');
    }

    applyTokenResponse(data);
  }, [applyTokenResponse]);

  const verifyMfa = useCallback(async (code: string) => {
    const { data } = await axios.post(`${API_URL}/auth/mfa/verify`, {
      mfaToken: state.mfaToken,
      code,
    });
    applyTokenResponse(data);
  }, [state.mfaToken, applyTokenResponse]);

  const logout = useCallback(() => {
    setState({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      requiresMfa: false,
      mfaToken: null,
    });
    sessionStorage.removeItem('auth');
  }, []);

  const ensureValidToken = useCallback(async (): Promise<string> => {
    if (state.accessToken && !isTokenExpired(state.accessToken)) {
      return state.accessToken;
    }

    if (!state.accessToken || !state.refreshToken) {
      logout();
      throw new Error('Session expired');
    }

    try {
      const { data } = await axios.post(`${API_URL}/auth/refresh`, {
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      });
      applyTokenResponse(data);
      return data.accessToken;
    } catch {
      logout();
      throw new Error('Session expired');
    }
  }, [state.accessToken, state.refreshToken, logout, applyTokenResponse]);

  return (
    <AuthContext.Provider value={{ ...state, login, verifyMfa, logout, ensureValidToken }}>
      {children}
    </AuthContext.Provider>
  );
}
